import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { persistUtmParams, getStoredUtmParams } from "../utils/utmStorage";

/**
 * Attribution parameter names captured from the checkout URL.
 */
export const UTM_PARAM_NAMES = [
  // Standard UTMs
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",

  "sub_source",
  "utm_creative",
  // Google Ads
  "campaign_id",
  "adgroup_id",
  "ad_id",
  "creative",
  "keyword",
  "device",
  "matchtype",
  "network",
  "placement",
  "gclid",
  "gad_source",
  "gad_campaignid",
  // Meta / other click IDs
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
  // Extra attribution
  "source_medium",
  "landing_page_url",
  "referrer_url",
  "first_touch_source",
  "latest_touch_source",
  "ad_created_at",
];

/**
 * Reads UTM/attribution params from the current URL's query string, holds them
 * in state, and persists them to sessionStorage (best-effort) so thank-you /
 * later pages can recover them after a redirect strips the query.
 */
export default function useUtmCapture() {
  const location = useLocation();
  const [utmParams, setUtmParams] = useState({});

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const captured = {};
    UTM_PARAM_NAMES.forEach((name) => {
      const value = urlParams.get(name);
      if (value) captured[name] = value;
    });
    // Landing page = the checkout page itself (the page the form was filled
    // on), not the page before it. The backend can't derive this — the
    // cross-origin create-intent request only carries the origin, not the
    // path — so it's captured here from the full URL and passed along.
    if (!captured.landing_page_url && typeof window !== "undefined") {
      captured.landing_page_url = window.location.href;
    }
    setUtmParams(captured);
    // Merge over anything stored earlier this session so a param-less page
    // (which now always has at least landing_page_url) can't wipe richer
    // earlier attribution.
    persistUtmParams({ ...getStoredUtmParams(), ...captured });
  }, [location.search]);

  return utmParams;
}
